import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

function splitList(s) {
  return String(s ?? '').split(',').map((x) => x.trim()).filter(Boolean);
}

function toDateInput(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function fromDateInput(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  // End-of-day UTC so the key works through the selected date
  return Date.UTC(y, m - 1, d, 23, 59, 59, 999);
}

function defaultState(row) {
  if (row) {
    return {
      key_name: row.key_name ?? '',
      key_description: row.key_description ?? '',
      owner_email: row.owner_email ?? '',
      owner_name: row.owner_name ?? '',
      owner_phone: row.owner_phone ?? '',
      allowed_user_agent: row.allowed_user_agent ?? '',
      allowed_origins: row.allowed_origins ?? '',
      allowed_endpoints: splitList(row.allowed_endpoints),
      allowed_referers: row.allowed_referers ?? '',
      allowed_ips: row.allowed_ips ?? '',
      rate_limit: row.rate_limit ?? 0,
      expires_at: toDateInput(row.expires_at),
      is_active: !!row.is_active
    };
  }
  return {
    key_name: '', key_description: '',
    owner_email: '', owner_name: '', owner_phone: '',
    allowed_user_agent: '', allowed_origins: '',
    allowed_endpoints: [], allowed_referers: '', allowed_ips: '',
    rate_limit: 0, expires_at: '', is_active: true
  };
}

export default function ApiKeyForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => defaultState(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [endpoints, setEndpoints] = useState([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    api.get('/datasets')
      .then((r) => { if (alive) setEndpoints(r.endpoints ?? []); })
      .catch(() => { /* leave list empty; UI will show a notice */ });
    return () => { alive = false; };
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleEndpoint = (e) =>
    setForm((f) => ({
      ...f,
      allowed_endpoints: f.allowed_endpoints.includes(e)
        ? f.allowed_endpoints.filter((x) => x !== e)
        : [...f.allowed_endpoints, e]
    }));

  async function submit(ev) {
    ev.preventDefault();
    setError('');
    setSaving(true);
    const payload = {
      ...form,
      allowed_endpoints: form.allowed_endpoints.join(','),
      expires_at: fromDateInput(form.expires_at),
      rate_limit: parseInt(form.rate_limit, 10) || 0
    };
    try {
      const saved = editing
        ? await api.patch(`/api-keys/${editing.id}`, payload)
        : await api.post('/api-keys', payload);
      onSaved(saved, { isNew: !editing });
    } catch (err) {
      setError(err?.data?.error ?? err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-end bg-ink-900/40 p-0" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-xl flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
          <h3 className="text-base font-semibold text-ink-900">
            {editing ? `Edit key #${editing.id}` : 'New API key'}
          </h3>
          <button className="btn-ghost p-2" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>

        <form onSubmit={submit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <Section title="Identity">
              <Field label="Key name">
                <input className="input" value={form.key_name} onChange={(e) => update({ key_name: e.target.value })} placeholder="e.g. Desktop client prod" />
              </Field>
              <Field label="Description">
                <textarea className="input min-h-[60px]" value={form.key_description} onChange={(e) => update({ key_description: e.target.value })} placeholder="Optional notes" />
              </Field>
            </Section>

            <Section title="Owner">
              <Field label="Email" required>
                <input className="input" type="email" required value={form.owner_email} onChange={(e) => update({ owner_email: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name"><input className="input" value={form.owner_name} onChange={(e) => update({ owner_name: e.target.value })} /></Field>
                <Field label="Phone"><input className="input" value={form.owner_phone} onChange={(e) => update({ owner_phone: e.target.value })} /></Field>
              </div>
            </Section>

            <Section title="Access rules">
              <Field label="Allowed endpoints" hint="Empty = all endpoints. List is taken from server config (.env DATASETS + services)">
                <div className="flex flex-wrap gap-2">
                  {endpoints.length === 0 && (
                    <span className="text-xs text-ink-500">Loading…</span>
                  )}
                  {endpoints.map((e) => (
                    <label key={e} className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${form.allowed_endpoints.includes(e) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-ink-200 text-ink-700 hover:border-ink-300'}`}>
                      <input type="checkbox" className="hidden" checked={form.allowed_endpoints.includes(e)} onChange={() => toggleEndpoint(e)} />
                      <span>{e}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Allowed origins" hint="Comma-separated. Supports *.domain.com and ? (single char)">
                <input className="input" value={form.allowed_origins} onChange={(e) => update({ allowed_origins: e.target.value })} placeholder="*.micro-gis.com, localhost" />
              </Field>
              <Field label="Allowed referers" hint="Comma-separated substrings, case-insensitive. Matches against the full Referer header (e.g. https://ad.micro-gis.com/map/).">
                <input className="input" value={form.allowed_referers} onChange={(e) => update({ allowed_referers: e.target.value })} placeholder="ad.micro-gis.com, example.com/maps" />
              </Field>
              <Field label="Allowed IPs" hint="Comma-separated IPv4 or CIDR. Empty = any IP. Use for clients without a domain (fixed office IP, server-to-server).">
                <input className="input" value={form.allowed_ips} onChange={(e) => update({ allowed_ips: e.target.value })} placeholder="93.171.154.61, 10.0.0.0/24" />
              </Field>
              <Field label="Allowed user-agent" hint="Substring, case-sensitive. Empty = any UA">
                <input className="input" value={form.allowed_user_agent} onChange={(e) => update({ allowed_user_agent: e.target.value })} placeholder="MyDesktopApp/1.0" />
              </Field>
            </Section>

            <Section title="Limits">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Rate limit (req/min)" hint="0 = unlimited">
                  <input className="input" type="number" min="0" value={form.rate_limit} onChange={(e) => update({ rate_limit: e.target.value })} />
                </Field>
                <Field
                  label="Expires (UTC)"
                  hint="Empty = no expiry. The selected day is stored as its end-of-day in UTC — the key stops working at 23:59:59 UTC of this date, which may land on a different calendar day in the key owner's local timezone."
                >
                  <input className="input" type="date" value={form.expires_at} onChange={(e) => update({ expires_at: e.target.value })} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => update({ is_active: e.target.checked })} />
                <span>Active</span>
              </label>
            </Section>

            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

            {editing && <UsageSection keyId={editing.id} />}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-100 px-6 py-3">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Saving…</> : (editing ? 'Save changes' : 'Create key')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">{title}</h4>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-ink-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <div className="mt-1 text-xs text-ink-500">{hint}</div>}
    </div>
  );
}

// Read-only usage breakdown for an existing key. Fetched on open and when
// the window selector changes. Uses the same compact aesthetic as the list
// view — no charting library, just numbers and a couple of tiny tables.
function UsageSection({ keyId }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get(`/api-keys/${keyId}/usage?days=${days}`)
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setErr(e.message || 'Failed to load usage'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [keyId, days]);

  const total = data?.total ?? { ok: 0, deny: 0, rate: 0 };
  const hasAny = total.ok + total.deny + total.rate > 0;

  return (
    <Section title="Usage">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 rounded-md border border-ink-200 p-0.5 text-xs">
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded px-2 py-1 ${days === d ? 'bg-brand-50 font-semibold text-brand-700' : 'text-ink-600 hover:bg-ink-50'}`}
            >
              {d}d
            </button>
          ))}
        </div>
        {data?.last_day && (
          <div className="text-xs text-ink-500">Last request: <span className="font-mono">{data.last_day}</span></div>
        )}
      </div>

      {err && <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

      <div className="grid grid-cols-3 gap-2">
        <Stat tone="emerald" label="OK" value={total.ok} />
        <Stat tone="red"     label="Deny" value={total.deny} />
        <Stat tone="amber"   label="Rate-limited" value={total.rate} />
      </div>

      {loading && !data ? (
        <div className="text-xs text-ink-500">Loading…</div>
      ) : !hasAny ? (
        <div className="text-xs text-ink-500">No recorded traffic in this window.</div>
      ) : (
        <>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-500">By endpoint</div>
            <table className="w-full text-xs">
              <tbody>
                {data.by_endpoint.map((r) => (
                  <tr key={r.endpoint || '(none)'} className="border-b border-ink-100 last:border-b-0">
                    <td className="py-1 font-mono text-ink-700">{r.endpoint || <em className="text-ink-400">(none)</em>}</td>
                    <td className="py-1 text-right font-mono">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-500">By day</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink-400">
                  <th className="py-1 font-normal">Day</th>
                  <th className="py-1 text-right font-normal">OK</th>
                  <th className="py-1 text-right font-normal">Deny</th>
                  <th className="py-1 text-right font-normal">Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((r) => (
                  <tr key={r.day} className="border-b border-ink-100 last:border-b-0">
                    <td className="py-1 font-mono text-ink-700">{r.day}</td>
                    <td className="py-1 text-right font-mono text-emerald-700">{r.ok}</td>
                    <td className="py-1 text-right font-mono text-red-700">{r.deny || ''}</td>
                    <td className="py-1 text-right font-mono text-amber-700">{r.rate || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

function Stat({ label, value, tone }) {
  const toneMap = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red:     'border-red-200 bg-red-50 text-red-700',
    amber:   'border-amber-200 bg-amber-50 text-amber-700'
  };
  return (
    <div className={`rounded-md border px-3 py-2 text-center ${toneMap[tone] || ''}`}>
      <div className="font-mono text-lg font-semibold">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}
